import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Wrench } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { formatIssueCopy } from "../utils/questionnaireIssueCopy";

import type { QuestionnaireValidationIssue } from "@digital-twins/shared-types";

interface ValidationIssueChipProps {
  questionId: string;
  validationIssues?: Array<QuestionnaireValidationIssue>;
}

/**
 * Blocking-issue indicator on a QuestionnaireQuestionCard header.
 * Distinct from TopicCoverageChip: labeled "Needs fix", wrench icon, never "!".
 */
export function ValidationIssueChip(props: ValidationIssueChipProps) {
  const { t } = useTranslation("app");
  const { questionId, validationIssues } = props;
  const firstIssue = validationIssues?.[0];
  const code = firstIssue?.code;

  useEffect(() => {
    if (!code) {
      return;
    }
    track(AnalyticsEvents.QUESTIONNAIRE_ISSUE_CHIP_VIEWED, {
      question_id: questionId,
      code,
    });
  }, [code, questionId]);

  if (!firstIssue) {
    return null;
  }

  const copy = formatIssueCopy(firstIssue);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border border-destructive/30 bg-destructive/15 px-[10px] py-[3px] text-[11px] font-medium leading-[1.4] text-destructive",
            )}
            data-testid={`questionnaire-validation-chip-${questionId}`}
            aria-label={t("questionnaire.needsFix")}
          >
            <Wrench className="h-3 w-3" />
            {t("questionnaire.needsFix")}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="max-w-[260px] text-[12px] leading-snug"
        >
          {copy.text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
